import React, { useState, useRef, useCallback } from 'react';
import {
  Box,
  Button,
  Typography,
  Paper,
  IconButton,
  Avatar,
  Stack,
  Alert
} from '@mui/material';
import {
  CloudUpload,
  Delete,
  Image as ImageIcon,
  DragIndicator
} from '@mui/icons-material';

type ImageUploadProps = {
  label: string;
  value?: string | File | null; // Can be URL string, File, or null
  onChange: (file: File | null) => void;
  disabled?: boolean;
  maxSize?: number; // Max file size in MB
  acceptedFormats?: string[]; // e.g., ['image/jpeg', 'image/png']
  previewSize?: number; // Preview image size
};

function ImageUpload({
  label,
  value,
  onChange,
  disabled = false,
  maxSize = 5, // 5MB default
  acceptedFormats = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'],
  previewSize = 120
}: ImageUploadProps) {
  const [dragActive, setDragActive] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Generate preview URL
  React.useEffect(() => {
    if (value instanceof File) {
      const objectUrl = URL.createObjectURL(value);
      setPreview(objectUrl);
      return () => URL.revokeObjectURL(objectUrl);
    } else if (typeof value === 'string' && value) {
      setPreview(value);
    } else {
      setPreview(null);
    }
  }, [value]);

  const validateFile = (file: File): string | null => {
    // Check file type
    if (!acceptedFormats.includes(file.type)) {
      return `Invalid file type. Accepted formats: ${acceptedFormats.join(', ')}`;
    }

    // Check file size
    if (file.size > maxSize * 1024 * 1024) {
      return `File size too large. Maximum size: ${maxSize}MB`;
    }

    return null;
  };

  const handleFile = (file: File) => {
    const validationError = validateFile(file);
    if (validationError) {
      setError(validationError);
      return;
    }

    setError(null);
    onChange(file);
  };

  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (disabled) return;

    const files = e.dataTransfer.files;
    if (files && files[0]) {
      handleFile(files[0]);
    }
  }, [disabled]);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (disabled) return;
    
    const files = e.target.files;
    if (files && files[0]) {
      handleFile(files[0]);
    }
  };

  const handleButtonClick = () => {
    if (disabled) return;
    fileInputRef.current?.click();
  };

  const handleRemove = () => {
    if (disabled) return;
    setError(null);
    onChange(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Typography variant="subtitle2" gutterBottom>
        {label}
      </Typography>
      
      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept={acceptedFormats.join(',')}
        onChange={handleFileSelect}
        style={{ display: 'none' }}
        disabled={disabled}
      />

      {/* Upload area */}
      <Paper
        elevation={1}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
        sx={{
          border: `2px dashed ${dragActive ? 'primary.main' : 'grey.300'}`,
          borderRadius: 2,
          p: 3,
          textAlign: 'center',
          cursor: disabled ? 'not-allowed' : 'pointer',
          backgroundColor: dragActive ? 'action.hover' : 'background.paper',
          opacity: disabled ? 0.6 : 1,
          transition: 'all 0.2s ease-in-out',
          '&:hover': {
            borderColor: disabled ? 'grey.300' : 'primary.main',
            backgroundColor: disabled ? 'background.paper' : 'action.hover'
          }
        }}
        onClick={handleButtonClick}
      >
        {preview ? (
          <Stack spacing={2} alignItems="center">
           <Avatar
            src={preview}
            sx={{ 
              width: previewSize, 
              height: previewSize,
              '& img': {
                objectFit: 'contain', // This will fit the entire image within the circle
                width: '100%',
                height: '100%'
              }
            }}
          >
            <ImageIcon />
          </Avatar>
            <Stack direction="row" spacing={1}>
              <Button
                variant="outlined"
                size="small"
                startIcon={<CloudUpload />}
                onClick={(e) => {
                  e.stopPropagation();
                  handleButtonClick();
                }}
                disabled={disabled}
              >
                Replace
              </Button>
              <IconButton
                size="small"
                color="error"
                onClick={(e) => {
                  e.stopPropagation();
                  handleRemove();
                }}
                disabled={disabled}
              >
                <Delete />
              </IconButton>
            </Stack>
          </Stack>
        ) : (
          <Stack spacing={2} alignItems="center">
            <Box sx={{ color: 'text.secondary' }}>
              {dragActive ? (
                <DragIndicator sx={{ fontSize: 48 }} />
              ) : (
                <CloudUpload sx={{ fontSize: 48 }} />
              )}
            </Box>
            <Stack spacing={1} alignItems="center">
              <Typography variant="body1" color="text.primary">
                {dragActive ? 'Drop image here' : 'Click to upload or drag & drop'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Supported: {acceptedFormats.map(f => f.split('/')[1].toUpperCase()).join(', ')}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Max size: {maxSize}MB
              </Typography>
            </Stack>
          </Stack>
        )}
      </Paper>

      {/* Error message */}
      {error && (
        <Alert severity="error" sx={{ mt: 1 }}>
          {error}
        </Alert>
      )}
    </Box>
  );
}

export default ImageUpload;